import { useState, useRef, useEffect } from 'react';
import { Bars3Icon } from '@heroicons/react/24/outline';
import { useTheme } from '../../context/ThemeContext';

/**
 * MobileViewSelector Component
 * 
 * A mobile-friendly view selector with:
 * - Title and current selection always visible
 * - Horizontal swipeable scroll for other options
 * - Dot indicators for navigation
 * - Hamburger menu option for quick access
 * 
 * @param {Array} views - Array of view objects: { value, label, icon }
 * @param {string} currentView - Currently selected view value
 * @param {Function} onViewChange - Callback when view changes
 * @param {string} title - Title for the selector (default: "Views")
 * @param {React.ReactNode} sharingButton - Optional sharing button to display in header
 */
const MobileViewSelector = ({ 
  views = [], 
  currentView, 
  onViewChange, 
  title = "Views",
  sharingButton = null
}) => {
  const { themeColors } = useTheme();
  const [showMenu, setShowMenu] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [canScroll, setCanScroll] = useState(false);
  const scrollContainerRef = useRef(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const isScrolling = useRef(false);

  // Find current view index
  const currentIndex = views.findIndex(v => v.value === currentView);
  const currentViewData = views[currentIndex] || views[0];

  // Calculate scroll indicators - recalculate when menu state changes
  const updateScrollState = () => {
    if (scrollContainerRef.current) {
      const scrollWidth = scrollContainerRef.current.scrollWidth || 0;
      const clientWidth = scrollContainerRef.current.clientWidth || 0;
      setCanScroll(scrollWidth > clientWidth);
    }
  };

  // Handle scroll position update
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      setScrollPosition(scrollContainerRef.current.scrollLeft);
      updateScrollState();
    }
  };

  // Recalculate scroll state when menu closes, component mounts, or views change
  useEffect(() => {
    if (!showMenu) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        setTimeout(() => {
          updateScrollState();
        }, 150);
      });
    }
  }, [showMenu, views.length]);

  // Also recalculate on mount
  useEffect(() => {
    updateScrollState();
  }, []);

  // Touch handlers for swipe
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    isScrolling.current = false;
  };

  const handleTouchMove = (e) => {
    if (!touchStartX.current || !touchStartY.current) return;
    
    const touchX = e.touches[0].clientX;
    const touchY = e.touches[0].clientY;
    const diffX = touchStartX.current - touchX;
    const diffY = touchStartY.current - touchY;

    // Determine if this is a horizontal scroll
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 10) {
      isScrolling.current = true;
    }
  };

  const handleTouchEnd = (e) => {
    if (!isScrolling.current) {
      // If not scrolling, treat as tap
      const touchX = e.changedTouches[0].clientX;
      const touchY = e.changedTouches[0].clientY;
      const element = document.elementFromPoint(touchX, touchY);
      const button = element?.closest('button');
      if (button && button.dataset.view) {
        onViewChange(button.dataset.view);
      }
    }
    touchStartX.current = 0;
    touchStartY.current = 0;
    isScrolling.current = false;
  };

  // Scroll to current view on mount/change
  useEffect(() => {
    if (scrollContainerRef.current && currentIndex >= 0) {
      const button = scrollContainerRef.current.querySelector(`[data-view="${currentView}"]`);
      if (button) {
        button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [currentView, currentIndex]);

  // Close menu when clicking outside
  useEffect(() => {
    if (showMenu) {
      const handleClickOutside = (e) => {
        if (!e.target.closest('.mobile-view-selector')) {
          setShowMenu(false);
        }
      };
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showMenu]);

  return (
    <div className="mobile-view-selector bg-white rounded-xl p-2 sm:p-3 md:p-4 mb-4 sm:mb-6 shadow-sm border border-gray-200 max-w-full md:max-w-full mx-auto">
      {/* Header: Title, Current Selection, Sharing Button, Hamburger */}
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <h3 className="text-xs font-semibold text-gray-700 whitespace-nowrap">{title}</h3>
          <span className="text-gray-400 text-xs">•</span>
          <div className="flex items-center gap-1 min-w-0 flex-1">
            {currentViewData?.icon && (
              <span className="flex-shrink-0" style={{ width: '14px', height: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ transform: 'scale(0.7)', display: 'inline-block' }}>
                  {currentViewData.icon}
                </span>
              </span>
            )}
            <span className="text-xs font-medium text-gray-900 truncate">
              {currentViewData?.label || 'Select View'}
            </span>
          </div>
        </div>
        
        <div className="flex items-center gap-1.5">
          {/* Sharing Button - Icon only with tooltip */}
          {sharingButton && (
            <div className="relative group">
              {sharingButton}
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                Sharing Settings
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
          )}
          
          {/* Hamburger Menu Button - Mobile Only */}
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="md:hidden rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0 flex items-center justify-center"
            style={{ padding: '4.8px' }}
            aria-label="Toggle menu"
          >
            <Bars3Icon className="h-4 w-4 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Hamburger Menu Dropdown - Mobile Only */}
      {showMenu && (
        <div className="md:hidden mb-2.5 border-t border-gray-200 pt-2.5">
          <div className="flex flex-col gap-1.5">
            {views.map((view) => (
              <button
                key={view.value}
                onClick={() => {
                  if (view.disabled) return;
                  onViewChange(view.value);
                  setShowMenu(false);
                }}
                disabled={view.disabled}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all text-left ${
                  view.disabled
                    ? 'bg-gray-200/50 text-gray-400 cursor-not-allowed'
                    : currentView === view.value
                    ? 'text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                style={currentView === view.value && !view.disabled ? {
                  backgroundColor: themeColors.primary,
                } : {}}
                title={view.title || view.label}
              >
                {view.icon && (
                  <span className="flex-shrink-0" style={{ width: '14px', height: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ transform: 'scale(0.7)', display: 'inline-block' }}>
                      {view.icon}
                    </span>
                  </span>
                )}
                <span>{view.label}</span>
                {currentView === view.value && !view.disabled && (
                  <span className="ml-auto text-xs">✓</span>
                )}
                {view.disabled && (
                  <span className="ml-auto text-xs">🔒</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Horizontal Scrollable View Options - Hide when hamburger menu is open */}
      {!showMenu && (
        <div className="relative">
          {/* Scrollable Container */}
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            className="flex gap-1.5 sm:gap-3 md:gap-4 overflow-x-auto md:overflow-x-visible scrollbar-hide pb-2 -mx-1 px-1 sm:justify-center md:justify-center"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {views.map((view) => (
              <button
                key={view.value}
                data-view={view.value}
                onClick={() => {
                  if (view.disabled) return;
                  onViewChange(view.value);
                }}
                disabled={view.disabled}
                className={`flex items-center justify-center gap-1 sm:gap-2 md:gap-2 px-2 sm:px-5 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-lg transition-all flex-1 sm:flex-initial md:flex-initial overflow-hidden min-w-0 ${
                  view.disabled
                    ? 'bg-gray-200/50 text-gray-400 cursor-not-allowed'
                    : currentView === view.value
                    ? 'text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                style={currentView === view.value && !view.disabled ? {
                  backgroundColor: themeColors.primary,
                } : {}}
                title={view.title || view.label}
              >
                {view.icon && (
                  <span className="flex-shrink-0 sm:w-5 sm:h-5 md:w-5 md:h-5" style={{ width: '12px', height: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span className="md:scale-100" style={{ display: 'inline-block' }}>
                      {view.icon}
                    </span>
                  </span>
                )}
                <span className="truncate md:truncate-none text-[10px] sm:text-sm md:text-base font-semibold whitespace-nowrap">
                  {view.label}
                </span>
              </button>
            ))}
          </div>

          {/* Scroll Indicators (Dots) - Only show if scrollable, much smaller with dimension */}
          {canScroll && (
            <div className="flex justify-center gap-0.5 mt-1">
              {views.map((_, index) => {
                const isActive = index === currentIndex;
                const isVisible = (() => {
                  if (!scrollContainerRef.current) return false;
                  const itemWidth = 90; // Approximate button width (reduced from 120)
                  const visibleStart = scrollPosition / itemWidth;
                  const visibleEnd = (scrollPosition + scrollContainerRef.current.clientWidth) / itemWidth;
                  return index >= Math.floor(visibleStart) && index <= Math.ceil(visibleEnd);
                })();

                return (
                  <button
                    key={index}
                    onClick={() => {
                      const button = scrollContainerRef.current?.querySelector(
                        `[data-view="${views[index].value}"]`
                      );
                      if (button) {
                        button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                      }
                    }}
                    className="rounded-full transition-all"
                    style={{
                      ...(isActive ? {
                        background: `linear-gradient(to right, ${themeColors.primary}, ${themeColors.secondary})`,
                        boxShadow: '0 0.5px 1px rgba(99, 102, 241, 0.4), inset 0 0.5px 0 rgba(255, 255, 255, 0.3)',
                        width: '15px',
                        height: '15px',
                        minWidth: '15px',
                        minHeight: '15px',
                      } : isVisible ? {
                        backgroundColor: '#9CA3AF',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                        width: '10px',
                        height: '10px',
                        minWidth: '10px',
                        minHeight: '10px',
                      } : {
                        backgroundColor: '#D1D5DB',
                        width: '10px',
                        height: '10px',
                        minWidth: '10px',
                        minHeight: '10px',
                      }),
                    }}
                    aria-label={`Go to ${views[index].label}`}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MobileViewSelector;

